/** Cascade date+orderKey updates when reordering the Plan-modus rail. */
import { reorderByActiveOver } from '@/features/today/utils/todayListReorderCompute';

/**
 * Reorders `tasks` by moving `activeId` next to `overId`, then redistributes the
 * existing date/orderKey slots within the affected range (blast radius) across the
 * new task order — the tasks shift through the existing slots rather than each
 * keeping its own date. Mirrors the time-repack pattern in
 * `todayListReorderCompute.js` (`repackTimedBlast`), but for calendar dates.
 * @param {Array<{id: string, date: any, orderKey: string}>} tasks
 * @param {string} activeId
 * @param {string} overId
 * @returns {Array<{id: string, date: any, orderKey: string}>}
 */
export const cascadeDateOrderUpdates = (tasks, activeId, overId) => {
  const result = reorderByActiveOver(tasks, activeId, overId);
  if (!result) return [];
  const { order, oldIndex, newIndex } = result;
  const blastStart = Math.min(oldIndex, newIndex);
  const blastEnd = Math.max(oldIndex, newIndex);

  const slots = tasks
    .slice(blastStart, blastEnd + 1)
    .map(({ date, orderKey }) => ({ date, orderKey }));

  const updates = [];
  for (let i = blastStart; i <= blastEnd; i += 1) {
    const slot = slots[i - blastStart];
    const task = order[i];
    if (task.date !== slot.date || task.orderKey !== slot.orderKey) {
      updates.push({ id: task.id, date: slot.date, orderKey: slot.orderKey });
    }
  }
  return updates;
};

/**
 * Keyboard variant: moves `activeId` one slot up/down within `railTasks`
 * (already sorted date-then-orderKey) and delegates to `cascadeDateOrderUpdates`.
 * @param {Array<{id: string, date: any, orderKey: string}>} railTasks
 * @param {string} activeId
 * @param {'up'|'down'} direction
 * @returns {Array<{id: string, date: any, orderKey: string}>}
 */
export const computePlanRailKeyboardReorderUpdates = (
  railTasks,
  activeId,
  direction,
) => {
  const activeIdx = railTasks.findIndex((task) => task.id === activeId);
  if (activeIdx === -1) return [];
  const overIdx = direction === 'down' ? activeIdx + 1 : activeIdx - 1;
  if (overIdx < 0 || overIdx >= railTasks.length) return [];
  return cascadeDateOrderUpdates(railTasks, activeId, railTasks[overIdx].id);
};
